import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Trash2, Save, Plus, RotateCcw, Shield, ScrollText, Copy, Edit2, X } from "lucide-react";
import { toast } from "sonner";

const RULE_TYPES = [
  { value: "percent_of_operation", label: "% від суми операції" },
  { value: "percent_of_salary_fund", label: "% від зарплатного фонду" },
  { value: "fixed", label: "Фіксована сума" },
];

function SalaryRules() {
  const [rules, setRules] = useState([]);
  const [fund, setFund] = useState(10);

  const load = async () => {
    const [r, f] = await Promise.all([api.get("/settings/salary-rules"), api.get("/settings/salary-fund")]);
    setRules(r.data); setFund(f.data.value);
  };
  useEffect(() => { load(); }, []);

  const update = (idx, key, val) => {
    const next = [...rules];
    next[idx] = { ...next[idx], [key]: val };
    setRules(next);
  };
  const save = async (r) => {
    try {
      const num = (v) => (v === "" || v === null || v === undefined ? null : parseFloat(v));
      await api.put(`/settings/salary-rules/${r.role}`, {
        role: r.role,
        type: r.type,
        value: parseFloat(r.value || 0),
        value_referred: num(r.value_referred),
        value_after_3m: num(r.value_after_3m),
        value_business_trip: num(r.value_business_trip),
      });
      toast.success("Збережено");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveFund = async () => {
    await api.put("/settings/salary-fund", { value: parseFloat(fund || 0) });
    toast.success("Фонд оновлено");
  };

  return (
    <div className="space-y-5">
      <Card className="gv-card"><CardContent className="p-5">
        <Label>Зарплатний фонд (% від вартості операції)</Label>
        <div className="flex gap-2 mt-2">
          <Input type="number" data-testid="salary-fund-input" value={fund} onChange={(e) => setFund(e.target.value)} className="max-w-xs" />
          <Button onClick={saveFund} data-testid="salary-fund-save" className="bg-[#0F2027]">Зберегти</Button>
        </div>
        <p className="text-xs text-slate-500 mt-2">За замовчуванням 10% від суми операції. Цей фонд розподіляється між учасниками.</p>
      </CardContent></Card>

      <Card className="gv-card"><CardContent className="p-0">
        <div className="p-5 border-b border-slate-200">
          <div className="font-semibold text-[#0F2027]">Формули по ролях</div>
          <div className="text-xs text-slate-500 mt-1">
            <strong>Iteration 19 — фіксовані відсотки, БЕЗ ділення між учасниками.</strong> Кожен працівник, вказаний у рекомендації, отримує СВІЙ ПОВНИЙ %.
            <ul className="list-disc list-inside mt-1 space-y-0.5">
              <li><strong className="text-sky-700">Первинний</strong> — Тип пацієнта = Первинний, рекомендація &lt; 90 днів (новий пацієнт).</li>
              <li><strong className="text-violet-700">Направлений</strong> — Тип пацієнта = Направлений.</li>
              <li><strong className="text-rose-700">Після 3 міс</strong> — первинний пацієнт з НОВОЮ рекомендацією після того, як попередня прострочилась (&gt;90 днів).</li>
              <li><strong className="text-teal-700">Відрядження</strong> — Бригада направлення (тільки якщо джерело має requires_team).</li>
            </ul>
            Залиште поле порожнім, щоб використовувати «Первинний» як фолбек.
          </div>
        </div>
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600 sticky top-0 z-10">
            <tr>
              <th className="text-left p-3">Роль</th>
              <th className="text-left p-3">Тип</th>
              <th className="text-left p-3 bg-sky-50 text-sky-700">Первинний</th>
              <th className="text-left p-3 bg-violet-50 text-violet-700">Направлений</th>
              <th className="text-left p-3 bg-rose-50 text-rose-700">Після 3 міс</th>
              <th className="text-left p-3 bg-teal-50 text-teal-700">Відрядження</th>
              <th className="text-right p-3 w-28"></th>
            </tr>
          </thead>
          <tbody>
            {rules.map((r, idx) => (
              <tr key={r.role} className="border-t border-slate-100">
                <td className="p-3 font-medium whitespace-nowrap">{r.role_label}</td>
                <td className="p-3">
                  <Select value={r.type} onValueChange={(v) => update(idx, "type", v)}>
                    <SelectTrigger className="min-w-[170px]"><SelectValue /></SelectTrigger>
                    <SelectContent>{RULE_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="p-3 bg-sky-50/30"><Input type="number" data-testid={`rule-${r.role}-value`} value={r.value ?? ""} onChange={(e) => update(idx, "value", e.target.value)} className="w-20" placeholder="0" /></td>
                <td className="p-3 bg-violet-50/30"><Input type="number" data-testid={`rule-${r.role}-referred`} value={r.value_referred ?? ""} onChange={(e) => update(idx, "value_referred", e.target.value)} className="w-20" placeholder="—" /></td>
                <td className="p-3 bg-rose-50/30"><Input type="number" data-testid={`rule-${r.role}-after`} value={r.value_after_3m ?? ""} onChange={(e) => update(idx, "value_after_3m", e.target.value)} className="w-20" placeholder="—" /></td>
                <td className="p-3 bg-teal-50/30"><Input type="number" data-testid={`rule-${r.role}-trip`} value={r.value_business_trip ?? ""} onChange={(e) => update(idx, "value_business_trip", e.target.value)} className="w-20" placeholder="—" /></td>
                <td className="p-3 text-right"><Button size="sm" onClick={() => save(r)} data-testid={`save-rule-${r.role}`} className="bg-[#0F2027]"><Save className="h-3 w-3 mr-1" />Зберегти</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </CardContent></Card>
    </div>
  );
}

function SimpleCRUD({ resource, fields, title, testid }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(Object.fromEntries(fields.map((f) => [f.key, f.default ?? ""])));

  const load = async () => { const r = await api.get(`/settings/${resource}`); setItems(r.data); };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try { await api.post(`/settings/${resource}`, form); toast.success("Додано");
      setForm(Object.fromEntries(fields.map((f) => [f.key, f.default ?? ""]))); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => { await api.delete(`/settings/${resource}/${id}`); toast.success("Видалено"); await load(); };

  return (
    <Card className="gv-card"><CardContent className="p-0">
      <div className="p-5 border-b border-slate-200"><div className="font-semibold text-[#0F2027]">{title}</div></div>
      <div className="p-5 border-b border-slate-200 grid grid-cols-1 md:grid-cols-4 lg:grid-cols-7 gap-3 items-end">
        {fields.map((f) => (
          <div key={f.key}><Label className="text-xs">{f.label}</Label>
            <Input type={f.type || "text"} value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: f.type === "number" ? parseFloat(e.target.value || 0) : e.target.value })} />
          </div>
        ))}
        <Button onClick={create} data-testid={`add-${testid}`} className="bg-[#0F2027]"><Plus className="h-4 w-4 mr-1" />Додати</Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600 sticky top-0 z-10">
            <tr>{fields.map((f) => <th key={f.key} className="text-left p-4">{f.label}</th>)}<th className="text-right p-4 w-20"></th></tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} className="border-t border-slate-100">
                {fields.map((f) => <td key={f.key} className="p-4">{it[f.key]}</td>)}
                <td className="p-4 text-right"><Button size="icon" variant="ghost" onClick={() => remove(it.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={fields.length + 1} className="p-6 text-center text-slate-400">Немає записів</td></tr>}
          </tbody>
        </table>
      </div>
    </CardContent></Card>
  );
}

function IolCatalog() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const EMPTY = {
    name: "", abbreviation: "", manufacturer: "", category: "", is_active: true,
    sale_price: 0, iol_cost: 0, medical_service: 0, anesthesiologist: 0,
  };
  const [form, setForm] = useState(EMPTY);
  const [manuFilter, setManuFilter] = useState("all");

  const load = async () => { const r = await api.get("/settings/iol-types"); setItems(r.data); };
  useEffect(() => { load(); }, []);

  const manufacturers = [...new Set(items.map((i) => i.manufacturer))].sort();
  const filtered = manuFilter === "all" ? items : items.filter((i) => i.manufacturer === manuFilter);

  const save = async () => {
    try {
      if (editing) await api.put(`/settings/iol-types/${editing.id}`, form);
      else await api.post("/settings/iol-types", form);
      toast.success("Збережено"); setOpen(false); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (it) => {
    if (!window.confirm(`Видалити ${it.name}?`)) return;
    await api.delete(`/settings/iol-types/${it.id}`); toast.success("Видалено"); await load();
  };
  const startCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const startEdit = (it) => { setEditing(it); setForm({ ...EMPTY, ...it }); setOpen(true); };

  return (
    <Card className="gv-card">
      <CardContent className="p-0">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="font-semibold text-[#0F2027]">Довідник ІОЛ</div>
            <div className="text-xs text-slate-500">{items.length} моделей · згідно прейскуранта Global Vision</div>
          </div>
          <div className="flex gap-2 items-center">
            <Select value={manuFilter} onValueChange={setManuFilter}>
              <SelectTrigger className="w-56" data-testid="iol-manu-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Усі виробники</SelectItem>
                {manufacturers.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={startCreate} data-testid="add-iol" className="bg-[#0F2027] hover:bg-[#0F2027]/90">
              <Plus className="h-4 w-4 mr-1" />Створити ІОЛ
            </Button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600 sticky top-0 z-10">
              <tr>
                <th className="text-left p-3">Виробник</th>
                <th className="text-left p-3">Назва</th>
                <th className="text-left p-3">Абрев.</th>
                <th className="text-left p-3">Категорія</th>
                <th className="text-right p-3">Ціна продажу</th>
                <th className="text-right p-3">Вартість ІОЛ</th>
                <th className="text-right p-3">Мед. обсл.</th>
                <th className="text-right p-3">Анестезіолог</th>
                <th className="text-center p-3">Активна</th>
                <th className="text-right p-3 w-24">Дії</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((it) => (
                <tr key={it.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                  <td className="p-3 text-xs font-semibold text-[#0D9488]">{it.manufacturer}</td>
                  <td className="p-3 font-medium text-[#0F2027]">{it.name}</td>
                  <td className="p-3 text-slate-600 font-mono text-xs">{it.abbreviation}</td>
                  <td className="p-3 text-xs text-slate-600">{it.category}</td>
                  <td className="p-3 text-right tabular-nums">{(it.sale_price || it.price || 0).toLocaleString("uk-UA")} ₴</td>
                  <td className="p-3 text-right tabular-nums text-slate-600">{(it.iol_cost || it.purchase_price || 0).toLocaleString("uk-UA")} ₴</td>
                  <td className="p-3 text-right tabular-nums text-slate-600">{(it.medical_service || 0).toLocaleString("uk-UA")} ₴</td>
                  <td className="p-3 text-right tabular-nums text-slate-600">{(it.anesthesiologist || 0).toLocaleString("uk-UA")} ₴</td>
                  <td className="p-3 text-center">
                    <Badge className={it.is_active !== false ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}>
                      {it.is_active !== false ? "Так" : "Ні"}
                    </Badge>
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex gap-1 justify-end">
                      <Button size="icon" variant="ghost" onClick={() => startEdit(it)} data-testid={`edit-iol-${it.id}`}><Edit2 className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => remove(it)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editing ? "Редагувати ІОЛ" : "Створити ІОЛ"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2"><Label>Назва</Label><Input data-testid="iol-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>Абревіатура</Label><Input value={form.abbreviation} onChange={(e) => setForm({ ...form, abbreviation: e.target.value })} /></div>
              <div><Label>Виробник</Label><Input value={form.manufacturer} onChange={(e) => setForm({ ...form, manufacturer: e.target.value })} /></div>
              <div><Label>Категорія</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
              <div><Label>Ціна продажу (₴)</Label><Input type="number" value={form.sale_price} onChange={(e) => setForm({ ...form, sale_price: parseFloat(e.target.value || 0) })} /></div>
              <div><Label>Вартість ІОЛ (₴)</Label><Input type="number" value={form.iol_cost} onChange={(e) => setForm({ ...form, iol_cost: parseFloat(e.target.value || 0) })} /></div>
              <div><Label>Медичне обслуговування (₴)</Label><Input type="number" value={form.medical_service} onChange={(e) => setForm({ ...form, medical_service: parseFloat(e.target.value || 0) })} /></div>
              <div><Label>Анестезіолог (₴)</Label><Input type="number" value={form.anesthesiologist} onChange={(e) => setForm({ ...form, anesthesiologist: parseFloat(e.target.value || 0) })} /></div>
              <div className="md:col-span-2 flex items-center gap-3">
                <input type="checkbox" id="iol-active" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
                <Label htmlFor="iol-active">Активна</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Скасувати</Button>
              <Button onClick={save} data-testid="save-iol" className="bg-[#0F2027]">Зберегти</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}


function RolesEditor() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const EMPTY = { key: "", label: "", description: "", is_active: true, is_system: false };
  const [form, setForm] = useState(EMPTY);

  const load = async () => { const r = await api.get("/settings/roles-catalog"); setItems(r.data); };
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const startEdit = (it) => { setEditing(it); setForm({ ...EMPTY, ...it }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) await api.put(`/settings/roles-catalog/${editing.key}`, form);
      else await api.post("/settings/roles-catalog", form);
      toast.success("Збережено"); setOpen(false); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const copyRole = async (it) => {
    try {
      await api.post(`/settings/roles-catalog/${it.key}/copy`);
      toast.success(`Скопійовано: ${it.label}`); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (it) => {
    if (!window.confirm(`Видалити роль "${it.label}"?`)) return;
    try {
      await api.delete(`/settings/roles-catalog/${it.key}`);
      toast.success("Видалено"); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Card className="gv-card">
      <CardContent className="p-0">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="font-semibold text-[#0F2027]">Ролі користувачів</div>
            <div className="text-xs text-slate-500">Системні ролі не можна видаляти. Усі зміни ролей пишуться в журнал змін.</div>
          </div>
          <Button onClick={startCreate} data-testid="create-role" className="bg-[#0F2027] hover:bg-[#0F2027]/90">
            <Plus className="h-4 w-4 mr-1" />Створити роль
          </Button>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600 sticky top-0 z-10">
            <tr>
              <th className="text-left p-3">Ключ</th>
              <th className="text-left p-3">Назва</th>
              <th className="text-left p-3">Опис</th>
              <th className="text-center p-3">Тип</th>
              <th className="text-center p-3">Активна</th>
              <th className="text-right p-3 w-44">Дії</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.key} className="border-t border-slate-100 hover:bg-slate-50/50">
                <td className="p-3 font-mono text-xs text-slate-600">{it.key}</td>
                <td className="p-3 font-medium text-[#0F2027]">{it.label}</td>
                <td className="p-3 text-slate-600 text-xs">{it.description || "—"}</td>
                <td className="p-3 text-center">
                  <Badge className={it.is_system ? "bg-slate-100 text-slate-600" : "bg-teal-50 text-teal-700"}>
                    {it.is_system ? "Системна" : "Користувацька"}
                  </Badge>
                </td>
                <td className="p-3 text-center">
                  <Badge className={it.is_active !== false ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}>
                    {it.is_active !== false ? "Так" : "Ні"}
                  </Badge>
                </td>
                <td className="p-3 text-right">
                  <div className="flex gap-1 justify-end">
                    <Button size="sm" variant="outline" onClick={() => startEdit(it)} data-testid={`edit-role-${it.key}`}>
                      <Edit2 className="h-3.5 w-3.5 mr-1" />Редагувати
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => copyRole(it)} data-testid={`copy-role-${it.key}`}>
                      <Copy className="h-3.5 w-3.5 mr-1" />Копія
                    </Button>
                    {!it.is_system && (
                      <Button size="icon" variant="ghost" onClick={() => remove(it)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-400">Немає ролей</td></tr>}
          </tbody>
        </table>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? "Редагувати роль" : "Створити роль"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Ключ (латиницею, без пробілів)</Label>
                <Input data-testid="role-key" value={form.key} disabled={!!editing}
                       onChange={(e) => setForm({ ...form, key: e.target.value })} placeholder="напр. senior_doctor" />
              </div>
              <div><Label>Назва</Label>
                <Input data-testid="role-label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Назва ролі українською" />
              </div>
              <div><Label>Опис</Label>
                <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <div className="p-3 border border-slate-200 rounded-md bg-slate-50/50">
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2 block">Тип ролі</Label>
                <div className="flex gap-3">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="radio"
                      name="role-type"
                      checked={!form.is_system}
                      onChange={() => setForm({ ...form, is_system: false })}
                      className="accent-[#0D9488] h-4 w-4"
                      data-testid="role-type-custom"
                    />
                    <Badge className="bg-teal-50 text-teal-700">Користувацька</Badge>
                    <span className="text-xs text-slate-500">— можна редагувати та видаляти</span>
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="radio"
                      name="role-type"
                      checked={!!form.is_system}
                      onChange={() => setForm({ ...form, is_system: true })}
                      className="accent-[#0D9488] h-4 w-4"
                      data-testid="role-type-system"
                    />
                    <Badge className="bg-slate-100 text-slate-600">Системна</Badge>
                    <span className="text-xs text-slate-500">— захищена від видалення</span>
                  </label>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="role-active" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
                <Label htmlFor="role-active">Активна</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Скасувати</Button>
              <Button onClick={save} data-testid="save-role" className="bg-[#0F2027]">Зберегти</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}


function PermissionsEditor() {
  const [data, setData] = useState({ permissions: [], roles: [] });
  const load = async () => { const r = await api.get("/settings/permissions"); setData(r.data); };
  useEffect(() => { load(); }, []);

  const toggle = (perm, role) => {
    const next = data.permissions.map((p) => {
      if (p.perm !== perm) return p;
      const has = p.roles.includes(role);
      return { ...p, roles: has ? p.roles.filter((r) => r !== role) : [...p.roles, role], _dirty: true };
    });
    setData({ ...data, permissions: next });
  };

  const save = async (perm) => {
    const p = data.permissions.find((x) => x.perm === perm);
    try {
      await api.put(`/settings/permissions/${encodeURIComponent(perm)}`, { roles: p.roles });
      toast.success(`Збережено: ${p.label}`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const reset = async (perm) => {
    try {
      await api.delete(`/settings/permissions/${encodeURIComponent(perm)}`);
      toast.success("Скинуто до за замовчуванням");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Card className="gv-card">
      <CardContent className="p-0">
        <div className="p-5 border-b border-slate-200">
          <div className="font-semibold text-[#0F2027]">Матриця прав доступу</div>
          <div className="text-xs text-slate-500 mt-1">
            Адміністратор завжди має повний доступ. Для решти ролей можна точково налаштувати доступ до модулів.
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600 sticky top-0">
              <tr>
                <th className="text-left p-3 min-w-[220px]">Право доступу</th>
                {data.roles.map((r) => (
                  <th key={r.value} className="text-center p-3 whitespace-nowrap">{r.label}</th>
                ))}
                <th className="text-right p-3 w-32">Дії</th>
              </tr>
            </thead>
            <tbody>
              {data.permissions.map((p) => (
                <tr key={p.perm} className="border-t border-slate-100 hover:bg-slate-50/50">
                  <td className="p-3">
                    <div className="font-medium text-[#0F2027]">{p.label}</div>
                    <div className="text-[10px] text-slate-400 font-mono">{p.perm}</div>
                    {p.overridden && <Badge className="bg-amber-50 text-amber-700 hover:bg-amber-50 mt-1 text-[10px]">змінено</Badge>}
                  </td>
                  {data.roles.map((r) => (
                    <td key={r.value} className="text-center p-3">
                      <Checkbox
                        checked={p.roles.includes(r.value)}
                        onCheckedChange={() => toggle(p.perm, r.value)}
                        data-testid={`perm-${p.perm}-${r.value}`}
                      />
                    </td>
                  ))}
                  <td className="p-3 text-right">
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" onClick={() => save(p.perm)} className="bg-[#0F2027] hover:bg-[#0F2027]/90" data-testid={`save-perm-${p.perm}`}>
                        <Save className="h-3 w-3 mr-1" />Зберегти
                      </Button>
                      {p.overridden && (
                        <Button size="icon" variant="ghost" onClick={() => reset(p.perm)} title="Скинути до за замовчуванням" data-testid={`reset-perm-${p.perm}`}>
                          <RotateCcw className="h-4 w-4 text-slate-500" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

const AUDIT_ACTION_LABEL = {
  login: "Вхід", logout: "Вихід",
  created: "Створено", updated: "Оновлено", deleted: "Видалено",
  payment_added: "Додано оплату", payout_created: "Виплата ЗП",
  permission_updated: "Зміна прав", permission_reset: "Скидання прав",
  salary_rule_updated: "Формула ЗП", salary_fund_updated: "Фонд ЗП",
};
const AUDIT_ENTITY_LABEL = {
  auth: "Авторизація", employee: "Працівник", patient: "Пацієнт",
  operation: "Операція", payment: "Виплата", settings: "Налаштування",
};

function AuditLog() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ actions: [], entities: [] });
  const [filters, setFilters] = useState({ action: "", entity: "", from_date: "", to_date: "" });

  const load = async () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
    const r = await api.get(`/audit?${params}`);
    setRows(r.data);
  };
  useEffect(() => {
    api.get("/audit/meta").then((r) => setMeta(r.data));
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const apply = () => load();
  const clear = () => { setFilters({ action: "", entity: "", from_date: "", to_date: "" }); setTimeout(load, 0); };

  return (
    <Card className="gv-card">
      <CardContent className="p-0">
        <div className="p-5 border-b border-slate-200 grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <div>
            <Label className="text-xs">Дія</Label>
            <Select value={filters.action || "all"} onValueChange={(v) => setFilters({ ...filters, action: v === "all" ? "" : v })}>
              <SelectTrigger data-testid="audit-action"><SelectValue placeholder="Усі" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Усі</SelectItem>
                {meta.actions.map((a) => <SelectItem key={a} value={a}>{AUDIT_ACTION_LABEL[a] || a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Об'єкт</Label>
            <Select value={filters.entity || "all"} onValueChange={(v) => setFilters({ ...filters, entity: v === "all" ? "" : v })}>
              <SelectTrigger data-testid="audit-entity"><SelectValue placeholder="Усі" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Усі</SelectItem>
                {meta.entities.map((a) => <SelectItem key={a} value={a}>{AUDIT_ENTITY_LABEL[a] || a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Від</Label><Input type="date" value={filters.from_date} onChange={(e) => setFilters({ ...filters, from_date: e.target.value })} /></div>
          <div><Label className="text-xs">До</Label><Input type="date" value={filters.to_date} onChange={(e) => setFilters({ ...filters, to_date: e.target.value })} /></div>
          <div className="flex gap-2">
            <Button onClick={apply} className="bg-[#0F2027] hover:bg-[#0F2027]/90 flex-1" data-testid="audit-apply">Застосувати</Button>
            <Button variant="outline" onClick={clear}>Скинути</Button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600 sticky top-0 z-10">
              <tr>
                <th className="text-left p-3 w-44">Час</th>
                <th className="text-left p-3">Користувач</th>
                <th className="text-left p-3">Дія</th>
                <th className="text-left p-3">Об'єкт</th>
                <th className="text-left p-3">Деталі</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                  <td className="p-3 text-slate-500 text-xs tabular-nums">{(r.created_at || "").slice(0, 19).replace("T", " ")}</td>
                  <td className="p-3"><div className="font-medium text-[#0F2027]">{r.user_name || r.user_email}</div><div className="text-xs text-slate-500">{r.user_role}</div></td>
                  <td className="p-3"><Badge className="bg-teal-50 text-teal-700 hover:bg-teal-50">{AUDIT_ACTION_LABEL[r.action] || r.action}</Badge></td>
                  <td className="p-3 text-slate-700">{AUDIT_ENTITY_LABEL[r.entity] || r.entity}{r.entity_id ? ` · ${r.entity_id.slice(0, 8)}…` : ""}</td>
                  <td className="p-3 text-xs text-slate-500 font-mono max-w-md truncate">{Object.keys(r.details || {}).length > 0 ? JSON.stringify(r.details) : "—"}</td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-slate-400">Немає записів</td></tr>}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function MaintenancePanel() {
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null);

  const purge = async () => {
    const confirm1 = window.confirm(
      "Видалити всі ТЕСТОВІ дані?\n\n" +
      "Буде видалено: рекомендації, операції, операційні дні, оплати, події пацієнтів.\n" +
      "Пацієнти, працівники, ролі, прейскурант, ІОЛ та налаштування ЗАЛИШАТЬСЯ.\n\n" +
      "Дію записано в журнал змін."
    );
    if (!confirm1) return;
    const confirm2 = window.prompt('Введіть "ВИДАЛИТИ" великими літерами, щоб підтвердити');
    if (confirm2 !== "ВИДАЛИТИ") { toast.error("Підтвердження некоректне"); return; }
    setBusy(true);
    try {
      const r = await api.post("/maintenance/purge-test-data");
      setResult(r.data?.deleted || {});
      toast.success("Тестові дані видалено");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Помилка"); }
    setBusy(false);
  };

  return (
    <Card className="gv-card">
      <CardContent className="p-6 space-y-4">
        <div>
          <div className="font-semibold text-[#0F2027]">Очистити тестові дані</div>
          <div className="text-sm text-slate-500 mt-1">
            Видаляє всі рекомендації, операції, операційні дні, оплати та події пацієнтів. Пацієнтів зберігає.
            Доступно лише адміністратору / директору.
          </div>
        </div>
        <Button onClick={purge} disabled={busy} data-testid="purge-test-data"
                className="bg-rose-600 hover:bg-rose-700 text-white">
          {busy ? "Видалення…" : "Очистити тестові дані"}
        </Button>
        {result && (
          <div className="text-xs bg-slate-50 border border-slate-200 rounded-md p-3">
            <div className="font-medium text-slate-700 mb-1">Видалено:</div>
            {Object.entries(result).map(([k, v]) => (
              <div key={k} className="text-slate-600">{k}: <b>{v}</b></div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


export default function Settings() {
  return (
    <div data-testid="settings-page" className="space-y-6">
      <div>
        <h1 className="heading text-3xl font-bold text-[#0F2027]">Налаштування</h1>
        <p className="text-sm text-slate-500">Формули зарплати, ІОЛ, операції, ролі</p>
      </div>

      <Tabs defaultValue="salary">
        <TabsList className="bg-slate-100 flex-wrap h-auto">
          <TabsTrigger value="salary" data-testid="tab-salary">Формули ЗП</TabsTrigger>
          <TabsTrigger value="iol" data-testid="tab-iol">Види ІОЛ</TabsTrigger>
          <TabsTrigger value="procedures" data-testid="tab-procedures">Процедури</TabsTrigger>
          <TabsTrigger value="sources" data-testid="tab-sources">Джерела направлення</TabsTrigger>
          <TabsTrigger value="marketing" data-testid="tab-marketing">Маркетингові джерела</TabsTrigger>
          <TabsTrigger value="iol-cat" data-testid="tab-iol-cat">Категорії ІОЛ</TabsTrigger>
          <TabsTrigger value="ops" data-testid="tab-ops">Види операцій</TabsTrigger>
          <TabsTrigger value="perms" data-testid="tab-perms"><Shield className="h-3.5 w-3.5 mr-1" />Права доступу</TabsTrigger>
          <TabsTrigger value="roles" data-testid="tab-roles"><Shield className="h-3.5 w-3.5 mr-1" />Ролі</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit"><ScrollText className="h-3.5 w-3.5 mr-1" />Журнал змін</TabsTrigger>
          <TabsTrigger value="maintenance" data-testid="tab-maintenance"><Shield className="h-3.5 w-3.5 mr-1" />Обслуговування</TabsTrigger>
        </TabsList>
        <TabsContent value="salary" className="mt-4"><SalaryRules /></TabsContent>
        <TabsContent value="iol" className="mt-4"><IolCatalog /></TabsContent>
        <TabsContent value="procedures" className="mt-4"><ProceduresCatalog /></TabsContent>
        <TabsContent value="sources" className="mt-4"><ReferralSourcesCatalog /></TabsContent>
        <TabsContent value="marketing" className="mt-4"><MarketingSourcesCatalog /></TabsContent>
        <TabsContent value="iol-cat" className="mt-4">
          <SimpleCRUD resource="iol-categories" testid="iol-cat" title="Категорії ІОЛ"
            fields={[{ key: "name", label: "Назва" }]} />
        </TabsContent>
        <TabsContent value="ops" className="mt-4">
          <SimpleCRUD resource="operation-types" testid="ops" title="Види операцій"
            fields={[
              { key: "name", label: "Назва" },
              { key: "default_price", label: "Ціна за замовч.", type: "number", default: 0 },
            ]} />
        </TabsContent>
        <TabsContent value="perms" className="mt-4"><PermissionsEditor /></TabsContent>
        <TabsContent value="roles" className="mt-4"><RolesEditor /></TabsContent>
        <TabsContent value="audit" className="mt-4"><AuditLog /></TabsContent>
        <TabsContent value="maintenance" className="mt-4"><MaintenancePanel /></TabsContent>
      </Tabs>
    </div>
  );
}

const PROC_CATEGORIES = [
  { value: "cataract", label: "Катаракта" },
  { value: "refractive", label: "Рефракційна хірургія" },
  { value: "laser", label: "Лазерна мікрохірургія" },
  { value: "intravitreal", label: "Інтравітреальні ін'єкції" },
  { value: "oculoplasty", label: "Окулопластика" },
];

function ProceduresCatalog() {
  const [items, setItems] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [form, setForm] = React.useState({ name: "", category: "cataract", is_active: true, comment: "" });
  const [showInactive, setShowInactive] = React.useState(true);

  const load = async () => {
    try {
      const r = await api.get(`/catalog/procedures?include_inactive=${showInactive}`);
      setItems(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Помилка"); }
  };
  React.useEffect(() => { load(); /* eslint-disable-next-line */ }, [showInactive]);

  const startCreate = () => { setEditing(null); setForm({ name: "", category: "cataract", is_active: true, comment: "" }); setOpen(true); };
  const startEdit = (it) => { setEditing(it); setForm({ name: it.name, category: it.category, is_active: it.is_active, comment: it.comment || "" }); setOpen(true); };
  const save = async () => {
    try {
      if (editing) await api.put(`/catalog/procedures/${editing.id}`, form);
      else await api.post("/catalog/procedures", form);
      toast.success("Збережено"); setOpen(false); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggle = async (it) => {
    try { await api.post(`/catalog/procedures/${it.id}/toggle`); toast.success(it.is_active ? "Деактивовано" : "Активовано"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (it) => {
    if (!window.confirm(`Видалити «${it.name}»? Якщо процедура використовується — буде помилка.`)) return;
    try { await api.delete(`/catalog/procedures/${it.id}`); toast.success("Видалено"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const grouped = items.reduce((acc, it) => { (acc[it.category] = acc[it.category] || []).push(it); return acc; }, {});

  return (
    <Card className="gv-card">
      <CardContent className="p-0">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="font-semibold text-[#0F2027]">Довідник процедур</div>
            <div className="text-xs text-slate-500 mt-1">Усі процедури в рекомендаціях / операціях беруться з цього списку. Для очних процедур створюйте окремі позиції OD та OS.</div>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs flex items-center gap-1 text-slate-600">
              <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} className="accent-[#0D9488]" />
              Показувати деактивовані
            </label>
            <Button onClick={startCreate} size="sm" data-testid="add-procedure" className="bg-[#0F2027]"><Plus className="h-4 w-4 mr-1" />Додати</Button>
          </div>
        </div>
        <div className="p-4 space-y-4">
          {PROC_CATEGORIES.map((cat) => {
            const list = grouped[cat.value] || [];
            if (list.length === 0) return null;
            return (
              <div key={cat.value}>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">{cat.label} ({list.length})</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {list.map((it) => (
                    <div key={it.id} className={`flex items-center justify-between gap-2 p-2 border rounded-md ${it.is_active ? "border-slate-200 bg-white" : "border-slate-100 bg-slate-50 opacity-60"}`} data-testid={`proc-row-${it.id}`}>
                      <div className="flex-1">
                        <div className="text-sm font-medium text-[#0F2027]">{it.name}</div>
                        {it.comment && <div className="text-xs text-slate-500">{it.comment}</div>}
                      </div>
                      {!it.is_active && <Badge className="bg-slate-200 text-slate-600">Неактивна</Badge>}
                      <Button size="icon" variant="ghost" onClick={() => startEdit(it)}><Edit2 className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => toggle(it)} title={it.is_active ? "Деактивувати" : "Активувати"}>
                        {it.is_active ? <X className="h-4 w-4 text-amber-600" /> : <Plus className="h-4 w-4 text-emerald-600" />}
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => remove(it)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Редагувати процедуру" : "Нова процедура"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Назва <span className="text-rose-500">*</span></Label>
              <Input data-testid="proc-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="напр. ФЕК OD" />
            </div>
            <div><Label>Категорія</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PROC_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Коментар</Label>
              <Input value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="accent-[#0D9488]" />
              Активна
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Скасувати</Button>
            <Button onClick={save} data-testid="proc-save" className="bg-[#0F2027]" disabled={!form.name?.trim()}>Зберегти</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}


function ReferralSourcesCatalog() {
  const [items, setItems] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [form, setForm] = React.useState({ key: "", label: "", requires_team: false, is_active: true, source_type: "regular", comment: "" });

  const load = async () => {
    try { const r = await api.get(`/catalog/referral-sources`); setItems(r.data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Помилка"); }
  };
  React.useEffect(() => { load(); }, []);

  const startCreate = () => { setEditing(null); setForm({ key: "", label: "", requires_team: false, is_active: true, source_type: "regular", comment: "" }); setOpen(true); };
  const startEdit = (it) => { setEditing(it); setForm({ key: it.key, label: it.label, requires_team: !!it.requires_team, is_active: !!it.is_active, source_type: it.source_type || "regular", comment: it.comment || "" }); setOpen(true); };
  const save = async () => {
    if (!form.key?.trim() || !form.label?.trim()) { toast.error("Заповніть ключ і назву"); return; }
    try {
      if (editing) await api.put(`/catalog/referral-sources/${editing.id}`, form);
      else await api.post("/catalog/referral-sources", form);
      toast.success("Збережено"); setOpen(false); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggle = async (it) => {
    try { await api.put(`/catalog/referral-sources/${it.id}`, { ...it, is_active: !it.is_active }); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (it) => {
    if (!window.confirm(`Видалити «${it.label}»? Якщо джерело використовується, операція не виконається.`)) return;
    try { await api.delete(`/catalog/referral-sources/${it.id}`); toast.success("Видалено"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Card className="gv-card">
      <CardContent className="p-0">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="font-semibold text-[#0F2027]">Джерела направлення</div>
            <div className="text-xs text-slate-500 mt-1">Якщо «Потребує бригаду» = так — у формі рекомендації з'явиться блок «Бригада направлення». Якщо «Тип» = Зовнішній лікар — з'явиться вибір зовнішнього лікаря.</div>
          </div>
          <Button onClick={startCreate} size="sm" data-testid="add-source" className="bg-[#0F2027]"><Plus className="h-4 w-4 mr-1" />Додати</Button>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600 sticky top-0 z-10">
            <tr>
              <th className="text-left p-3">Ключ</th>
              <th className="text-left p-3">Назва</th>
              <th className="text-left p-3">Тип</th>
              <th className="text-center p-3">Потребує бригаду</th>
              <th className="text-left p-3">Коментар</th>
              <th className="text-center p-3">Стан</th>
              <th className="text-right p-3 w-32">Дії</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} className="border-t border-slate-100" data-testid={`src-row-${it.id}`}>
                <td className="p-3 font-mono text-xs">{it.key}</td>
                <td className="p-3 font-medium">{it.label}</td>
                <td className="p-3 text-xs">{it.source_type === "external_doctor" ? <Badge className="bg-violet-50 text-violet-700">Зовнішній лікар</Badge> : <Badge variant="outline">Звичайне</Badge>}</td>
                <td className="p-3 text-center">{it.requires_team ? <Badge className="bg-emerald-50 text-emerald-700">Так</Badge> : <Badge className="bg-slate-100 text-slate-600">Ні</Badge>}</td>
                <td className="p-3 text-xs text-slate-500">{it.comment || "—"}</td>
                <td className="p-3 text-center">{it.is_active ? <Badge className="bg-emerald-50 text-emerald-700">Активне</Badge> : <Badge className="bg-slate-100 text-slate-500">Неактивне</Badge>}</td>
                <td className="p-3 text-right">
                  <div className="flex gap-1 justify-end">
                    <Button size="icon" variant="ghost" onClick={() => startEdit(it)}><Edit2 className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => toggle(it)} title={it.is_active ? "Деактивувати" : "Активувати"}>
                      {it.is_active ? <X className="h-4 w-4 text-amber-600" /> : <Plus className="h-4 w-4 text-emerald-600" />}
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(it)} title="Видалити"><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-400">Немає джерел</td></tr>}
          </tbody>
        </table>
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Редагувати джерело" : "Нове джерело"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Ключ (англ., унікальний) <span className="text-rose-500">*</span></Label>
              <Input data-testid="src-key" value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} placeholder="напр. optic_partner" disabled={!!editing} />
            </div>
            <div><Label>Назва <span className="text-rose-500">*</span></Label>
              <Input data-testid="src-label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="напр. Оптика" />
            </div>
            <div>
              <Label>Тип джерела</Label>
              <Select value={form.source_type} onValueChange={(v) => setForm({ ...form, source_type: v })}>
                <SelectTrigger data-testid="src-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="regular">Звичайне (просто джерело)</SelectItem>
                  <SelectItem value="external_doctor">Зовнішній лікар (з довідника)</SelectItem>
                </SelectContent>
              </Select>
              <div className="text-xs text-slate-500 mt-1">Якщо «Зовнішній лікар» — у формі рекомендації з'явиться вибір лікаря з довідника.</div>
            </div>
            <div><Label>Коментар</Label>
              <Input value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.requires_team} onChange={(e) => setForm({ ...form, requires_team: e.target.checked })} className="accent-[#0D9488]" data-testid="src-requires-team" />
              Потребує бригаду направлення (для нарахування ЗП)
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="accent-[#0D9488]" />
              Активне
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Скасувати</Button>
            <Button onClick={save} data-testid="src-save" className="bg-[#0F2027]" disabled={!form.key?.trim() || !form.label?.trim()}>Зберегти</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function ExternalDoctorsCatalog() {
  const [items, setItems] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [form, setForm] = React.useState({ full_name: "", phone: "", speciality: "", city: "", clinic: "", comment: "", is_active: true });

  const load = async () => {
    try { const r = await api.get(`/catalog/external-doctors`); setItems(r.data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Помилка"); }
  };
  React.useEffect(() => { load(); }, []);

  const startCreate = () => { setEditing(null); setForm({ full_name: "", phone: "", speciality: "", city: "", clinic: "", comment: "", is_active: true }); setOpen(true); };
  const startEdit = (it) => { setEditing(it); setForm({ ...form, ...it }); setOpen(true); };
  const save = async () => {
    if (!form.full_name?.trim()) { toast.error("Введіть ПІБ"); return; }
    try {
      if (editing) await api.put(`/catalog/external-doctors/${editing.id}`, form);
      else await api.post("/catalog/external-doctors", form);
      toast.success("Збережено"); setOpen(false); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggle = async (it) => {
    try { await api.put(`/catalog/external-doctors/${it.id}`, { ...it, is_active: !it.is_active }); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (it) => {
    if (!window.confirm(`Видалити лікаря «${it.full_name}»?`)) return;
    try { await api.delete(`/catalog/external-doctors/${it.id}`); toast.success("Видалено"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Card className="gv-card">
      <CardContent className="p-0">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="font-semibold text-[#0F2027]">Зовнішні лікарі</div>
            <div className="text-xs text-slate-500 mt-1">Лікарі з інших клінік / організацій, що направляють пацієнтів. НЕ є працівниками Global Vision.</div>
          </div>
          <Button onClick={startCreate} size="sm" data-testid="add-ext-doctor" className="bg-[#0F2027]"><Plus className="h-4 w-4 mr-1" />Додати</Button>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600 sticky top-0 z-10">
            <tr>
              <th className="text-left p-3">ПІБ</th>
              <th className="text-left p-3">Телефон</th>
              <th className="text-left p-3">Спеціальність</th>
              <th className="text-left p-3">Місто</th>
              <th className="text-left p-3">Клініка</th>
              <th className="text-center p-3">Стан</th>
              <th className="text-right p-3 w-32">Дії</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} className="border-t border-slate-100" data-testid={`ext-doc-row-${it.id}`}>
                <td className="p-3 font-medium">{it.full_name}</td>
                <td className="p-3 text-xs">{it.phone || "—"}</td>
                <td className="p-3 text-xs">{it.speciality || "—"}</td>
                <td className="p-3 text-xs">{it.city || "—"}</td>
                <td className="p-3 text-xs">{it.clinic || "—"}</td>
                <td className="p-3 text-center">{it.is_active ? <Badge className="bg-emerald-50 text-emerald-700">Активний</Badge> : <Badge className="bg-slate-100 text-slate-500">Неактивний</Badge>}</td>
                <td className="p-3 text-right">
                  <div className="flex gap-1 justify-end">
                    <Button size="icon" variant="ghost" onClick={() => startEdit(it)}><Edit2 className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => toggle(it)}>
                      {it.is_active ? <X className="h-4 w-4 text-amber-600" /> : <Plus className="h-4 w-4 text-emerald-600" />}
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(it)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-400">Немає зовнішніх лікарів</td></tr>}
          </tbody>
        </table>
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Редагувати зовнішнього лікаря" : "Новий зовнішній лікар"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2"><Label>ПІБ <span className="text-rose-500">*</span></Label>
              <Input data-testid="ext-doc-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
            </div>
            <div><Label>Телефон</Label>
              <Input data-testid="ext-doc-phone" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div><Label>Спеціальність</Label>
              <Input data-testid="ext-doc-speciality" value={form.speciality || ""} onChange={(e) => setForm({ ...form, speciality: e.target.value })} />
            </div>
            <div><Label>Місто</Label>
              <Input data-testid="ext-doc-city" value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} />
            </div>
            <div><Label>Клініка / місце роботи</Label>
              <Input data-testid="ext-doc-clinic" value={form.clinic || ""} onChange={(e) => setForm({ ...form, clinic: e.target.value })} />
            </div>
            <div className="md:col-span-2"><Label>Коментар</Label>
              <Input value={form.comment || ""} onChange={(e) => setForm({ ...form, comment: e.target.value })} />
            </div>
            <label className="md:col-span-2 flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="accent-[#0D9488]" />
              Активний
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Скасувати</Button>
            <Button onClick={save} data-testid="ext-doc-save" className="bg-[#0F2027]" disabled={!form.full_name?.trim()}>Зберегти</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function MarketingSourcesCatalog() {
  const [items, setItems] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [form, setForm] = React.useState({ label: "", comment: "", is_active: true });

  const load = async () => {
    try { const r = await api.get(`/catalog/marketing-sources`); setItems(r.data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Помилка"); }
  };
  React.useEffect(() => { load(); }, []);

  const startCreate = () => { setEditing(null); setForm({ label: "", comment: "", is_active: true }); setOpen(true); };
  const startEdit = (it) => { setEditing(it); setForm({ label: it.label, comment: it.comment || "", is_active: !!it.is_active }); setOpen(true); };
  const save = async () => {
    if (!form.label?.trim()) { toast.error("Введіть назву"); return; }
    try {
      if (editing) await api.put(`/catalog/marketing-sources/${editing.id}`, form);
      else await api.post("/catalog/marketing-sources", form);
      toast.success("Збережено"); setOpen(false); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggle = async (it) => {
    try { await api.put(`/catalog/marketing-sources/${it.id}`, { ...it, is_active: !it.is_active }); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (it) => {
    if (!window.confirm(`Видалити «${it.label}»?`)) return;
    try { await api.delete(`/catalog/marketing-sources/${it.id}`); toast.success("Видалено"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Card className="gv-card">
      <CardContent className="p-0">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="font-semibold text-[#0F2027]">Маркетингові джерела (Звідки дізнався)</div>
            <div className="text-xs text-slate-500 mt-1">Використовується тільки в картці пацієнта для маркетингової аналітики. НЕ впливає на нарахування зарплати.</div>
          </div>
          <Button onClick={startCreate} size="sm" data-testid="add-marketing" className="bg-[#0F2027]"><Plus className="h-4 w-4 mr-1" />Додати</Button>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600 sticky top-0 z-10">
            <tr>
              <th className="text-left p-3">Назва</th>
              <th className="text-left p-3">Коментар</th>
              <th className="text-center p-3">Стан</th>
              <th className="text-right p-3 w-32">Дії</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} className="border-t border-slate-100" data-testid={`mkt-row-${it.id}`}>
                <td className="p-3 font-medium">{it.label}</td>
                <td className="p-3 text-xs text-slate-500">{it.comment || "—"}</td>
                <td className="p-3 text-center">{it.is_active ? <Badge className="bg-emerald-50 text-emerald-700">Активне</Badge> : <Badge className="bg-slate-100 text-slate-500">Неактивне</Badge>}</td>
                <td className="p-3 text-right">
                  <div className="flex gap-1 justify-end">
                    <Button size="icon" variant="ghost" onClick={() => startEdit(it)}><Edit2 className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => toggle(it)}>
                      {it.is_active ? <X className="h-4 w-4 text-amber-600" /> : <Plus className="h-4 w-4 text-emerald-600" />}
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(it)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-slate-400">Немає джерел</td></tr>}
          </tbody>
        </table>
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Редагувати маркетингове джерело" : "Нове маркетингове джерело"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Назва <span className="text-rose-500">*</span></Label>
              <Input data-testid="mkt-label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="напр. Instagram" />
            </div>
            <div><Label>Коментар</Label>
              <Input value={form.comment || ""} onChange={(e) => setForm({ ...form, comment: e.target.value })} />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="accent-[#0D9488]" />
              Активне
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Скасувати</Button>
            <Button onClick={save} data-testid="mkt-save" className="bg-[#0F2027]" disabled={!form.label?.trim()}>Зберегти</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

